import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Film, Info, Play } from "lucide-react";
import { Particles } from "@/components/catalog/Particles";
import { MyListButton } from "@/components/catalog/MyListButton";
import { TrailerModal } from "@/components/catalog/TrailerModal";
import { fmtRuntime } from "@/lib/format";
import { usePlay } from "@/lib/play";

const HeroTitle = ({ text }) => {
  const [first, ...rest] = text.split(":");
  return (
    <h1 data-testid="hero-title" className="font-display leading-[0.92] tracking-[0.03em] text-[2.6rem] min-[400px]:text-[3rem] sm:text-7xl lg:text-[5rem] xl:text-[5.75rem] 2xl:text-[6.5rem] lg:whitespace-nowrap drop-shadow-[0_8px_40px_rgba(0,0,0,0.9)]" style={{ fontWeight: 900 }}>
      {rest.length ? (
        <>
          <span className="crimson-text block lg:inline">{first}:</span>{" "}
          <span className="gold-text block lg:inline">{rest.join(":").trim()}</span>
        </>
      ) : (
        <span className="gold-text">{text}</span>
      )}
    </h1>
  );
};

export const Hero = ({ title }) => {
  const navigate = useNavigate();
  const play = usePlay();
  const [trailer, setTrailer] = useState(false);
  const meta = [title.year, ...title.genres.slice(0, 2), fmtRuntime(title.runtime_min)].filter(Boolean);
  const chapterLine = [title.chapter, title.subtitle_tag].filter(Boolean).join(" — ");

  return (
    <section data-testid="home-hero" className="relative h-[74vh] min-h-[600px] md:h-[88vh] md:min-h-[680px] max-h-[1040px] w-full overflow-hidden bg-[#050505]">
      <img src={title.hero_url || title.backdrop_url} alt={title.title} fetchPriority="high" className="hero-zoom absolute inset-0 h-full w-full object-cover object-[66%_center] md:object-[70%_35%]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent md:via-[#050505]/40" />
      <div className="absolute inset-x-0 bottom-0 h-[62%] bg-gradient-to-t from-[#050505] via-[#050505]/85 to-transparent" />
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#050505]/90 to-transparent" />
      <div className="absolute inset-0 shadow-[inset_0_0_200px_rgba(0,0,0,0.85)]" />
      <Particles />
      <div className="relative z-10 mx-auto flex h-full max-w-[1680px] items-end px-4 sm:px-6 lg:px-10 pb-24 sm:pb-32 md:pb-44">
        <div className="w-full max-w-[46rem] lg:max-w-none space-y-3.5 md:space-y-5">
          <p className="eyebrow flex items-center gap-2.5 !text-[0.68rem] sm:!text-xs !tracking-[0.42em] fade-up" style={{ animationDelay: "0.1s" }}>
            <Crown className="h-4 w-4 flex-none" /> {title.badge || "AETRYX ORIGINAL"}
            <span className="hidden sm:block h-px w-16 bg-gradient-to-r from-[#D4AF37] to-transparent" />
          </p>
          <div className="fade-up" style={{ animationDelay: "0.25s" }}><HeroTitle text={title.title} /></div>
          {chapterLine && (
            <p data-testid="hero-chapter" className="font-display text-[0.72rem] sm:text-sm md:text-base tracking-[0.38em] sm:tracking-[0.5em] text-[#F5C451] fade-up" style={{ animationDelay: "0.35s" }}>
              {chapterLine}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 text-sm sm:text-base text-white/85 max-w-[46rem] fade-up" style={{ animationDelay: "0.45s" }} data-testid="hero-meta">
            {meta.map((m, i) => (
              <span key={m} className="flex items-center gap-2.5">{i > 0 && <span className="text-[#D4AF37]">·</span>}{m}</span>
            ))}
            <span className="chip">{title.maturity_rating}</span>
            <span className="chip">{title.quality}</span>
            <span className="chip">{title.audio}</span>
          </div>
          <p data-testid="hero-description" className="max-w-xl text-[0.94rem] sm:text-base leading-relaxed text-white/75 line-clamp-3 md:line-clamp-none fade-up" style={{ animationDelay: "0.55s" }}>{title.description}</p>
          <div className="grid grid-cols-3 gap-2.5 pt-1 sm:flex sm:flex-wrap sm:gap-3 fade-up" style={{ animationDelay: "0.65s" }}>
            <button data-testid="hero-watch-now-btn" onClick={() => play(title.slug)} className="btn-gold col-span-3 px-8 sm:px-10 py-3 text-[0.95rem] sm:text-base">
              <Play className="h-5 w-5 fill-current" /> Watch Now
            </button>
            <button data-testid="hero-trailer-btn" onClick={() => setTrailer(true)} className="btn-ghost-gold px-3 sm:px-6 py-3 text-sm sm:text-base">
              <Film className="h-5 w-5" /> Trailer
            </button>
            <MyListButton title={title} testId="hero-my-list-btn" />
            <button data-testid="hero-more-info-btn" onClick={() => navigate(`/title/${title.slug}`)} className="btn-ghost-gold px-3 sm:px-6 py-3 text-sm sm:text-base">
              <Info className="h-5 w-5" /> <span className="sm:hidden">Info</span><span className="hidden sm:inline">More Info</span>
            </button>
          </div>
        </div>
      </div>
      <TrailerModal title={title} open={trailer} onOpenChange={setTrailer} />
    </section>
  );
};
